import React, { useState, useRef, useEffect, useCallback } from 'react'
import injectStyles from 'react-jss'
import classNames from 'classnames'
import { styles } from './styles'
import CarouselIndicators from './CarouselIndicators'

const CampaignCarousel = ({ classes, campaigns }) => {
  const scrollContainerRef = useRef(null)
  const [currentPage, setCurrentPage] = useState(0)

  // Simple configuration
  const cardsPerPage = 2
  const totalPages = Math.ceil(campaigns.length / cardsPerPage)
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768

  /**
   * Update current page based on scroll position
   */
  const updateScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollLeft = container.scrollLeft
    const containerWidth = container.offsetWidth

    // Calculate page based on scroll position relative to container width
    const scrollableWidth = container.scrollWidth - containerWidth
    const scrollPercentage =
      scrollableWidth > 0 ? scrollLeft / scrollableWidth : 0

    const newPage = Math.round(scrollPercentage * (totalPages - 1))
    setCurrentPage(Math.min(Math.max(0, newPage), totalPages - 1))
  }, [totalPages])

  /**
   * Scroll to page - CSS snap alignment handles precise positioning
   */
  const scrollToPage = useCallback(
    pageIndex => {
      const container = scrollContainerRef.current
      if (!container) return

      const containerWidth = container.offsetWidth
      const scrollableWidth = container.scrollWidth - containerWidth

      // Calculate scroll position as a percentage of total scrollable width
      // The snap points (start/center/end) will handle exact alignment
      const scrollPercentage = totalPages > 1 ? pageIndex / (totalPages - 1) : 0
      const scrollAmount = scrollPercentage * scrollableWidth

      container.scrollTo({
        left: scrollAmount,
        behavior: 'smooth',
      })
    },
    [totalPages],
  )

  const handlePrevious = useCallback(() => {
    if (currentPage > 0) {
      scrollToPage(currentPage - 1)
    }
  }, [currentPage, scrollToPage])

  const handleNext = useCallback(() => {
    if (currentPage < totalPages - 1) {
      scrollToPage(currentPage + 1)
    }
  }, [currentPage, totalPages, scrollToPage])

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'ArrowLeft') {
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        handleNext()
      }
    },
    [handlePrevious, handleNext],
  )

  /**
   * Set up scroll listener
   */
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener('scroll', updateScrollPosition)
    updateScrollPosition()

    return () => {
      container.removeEventListener('scroll', updateScrollPosition)
    }
  }, [updateScrollPosition])

  /**
   * Recalculate on window resize
   */
  useEffect(() => {
    const handleResize = () => {
      updateScrollPosition()
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [updateScrollPosition])

  const showControls = campaigns.length > 2

  // Determine which mask to apply based on current page (desktop only)
  const isAtStart = currentPage === 0
  const isAtEnd = currentPage === totalPages - 1

  // Calculate fade class (only for desktop, mobile has no fades)
  let fadeClass = null
  if (showControls && !isMobile) {
    if (!isAtStart && !isAtEnd) {
      fadeClass = classes.fadeBoth // Middle pages: fade both sides
    } else if (!isAtStart) {
      fadeClass = classes.fadeLeft // Last page: fade left only
    } else if (!isAtEnd) {
      fadeClass = classes.fadeRight // First page: fade right only
    }
  }

  /**
   * Group campaigns into pages (2 cards per page)
   * Desktop: Each page gets different scroll-snap-align based on position (start/center/end)
   * Mobile: All pages centered
   */
  const renderCampaigns = () => {
    const grouped = []

    for (let i = 0; i < campaigns.length; i += cardsPerPage) {
      const pageIndex = Math.floor(i / cardsPerPage)

      // Determine scroll-snap-align based on page position
      // Mobile: always center (no sneakpeeks)
      // Desktop: dynamic alignment for proper sneakpeeks
      let snapAlign = 'center'
      if (!isMobile) {
        if (pageIndex === 0) {
          snapAlign = 'start'
        } else if (pageIndex === totalPages - 1) {
          snapAlign = 'end'
        } else {
          snapAlign = 'center'
        }
      }

      // Get the cards for this page
      const pageCards = []
      for (let j = 0; j < cardsPerPage && i + j < campaigns.length; j++) {
        pageCards.push(
          <div
            key={`card-${i + j}`}
            className={classes.cardWrapper}
            cypress_id={`campaignCarouselCard-${i + j}`}
          >
            {campaigns[i + j]}
          </div>,
        )
      }

      // Create page group with appropriate class and snap alignment
      grouped.push(
        <div
          key={`page-${pageIndex}`}
          className={
            isMobile ? classes.mobilePageGroup : classes.desktopPageGroup
          }
          style={{ scrollSnapAlign: snapAlign }}
          cypress_id={`campaignCarouselPage-${pageIndex}`}
        >
          {pageCards}
        </div>,
      )
    }

    return grouped
  }

  return (
    <div
      className={classNames(classes.carouselWrapper, fadeClass)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Recent campaigns carousel"
    >
      <div
        ref={scrollContainerRef}
        className={classes.scrollContainer}
        cypress_id="campaignCarouselContainer"
      >
        <div className={classes.cardsContainer}>{renderCampaigns()}</div>
      </div>

      {showControls && (
        <CarouselIndicators
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={scrollToPage}
          onPrevious={handlePrevious}
          onNext={handleNext}
          isAtStart={isAtStart}
          isAtEnd={isAtEnd}
        />
      )}
    </div>
  )
}

export default injectStyles(styles)(CampaignCarousel)
